import React, { useState } from 'react';
import Localize from 'react-intl-universal';

import { Formik, Form } from 'formik';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { sortByKey } from '@pages/EventDetails/Util';

import ConsentsList from './ConsentsList';
import AdditionalInformationTab from './DialogTabs/AdditionalInformationTab';
import ContactInformationTab from './DialogTabs/ContactInformationTab';
import GeneralInformationTab from './DialogTabs/GeneralInformationTab';
import { getInitialValues, extractMetaData } from './hooks/useParticipantInitialValues';

const TabPanel = ({ children, value, index }) => {
  return value === index ? <Box sx={{ mt: 2 }}>{children}</Box> : null;
};

const BookingParticipantDialog = ({ open, onClose, participantData }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const theme = useTheme();

  const {
    titles,
    genders,
    languages,
    countriesGeneral,
    regionsGeneral,
    countriesCommunication,
    regionsCommunication
  } = extractMetaData(participantData);
  const initialValues = getInitialValues(participantData);

  const sortByOrderNumber = sortByKey('orderNumber');
  const sortedEventConsents = sortByOrderNumber(participantData?.attendeeDto?.eventConsents);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth disableEscapeKeyDown>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme?.palette?.booking?.background
        }}
      >
        <Typography>{Localize.get('Booking.participantInfo')}</Typography>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            color: '#000'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ backgroundColor: theme?.palette?.booking?.background, minHeight: '30rem' }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, newIndex) => setTabIndex(newIndex)}
          variant='scrollable'
          scrollButtons='auto'
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: theme?.palette?.booking?.background,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500
            },
            '& .Mui-selected': {
              color: '#000',
              borderBottom: '2px solid #1976d2'
            }
          }}
        >
          <Tab label={Localize.get('AppNavBar.genInfo')} />
          <Tab label={Localize.get('Booking.contactInfo')} />
          <Tab label={Localize.get('Booking.additionalInformation')} />
          {!!sortedEventConsents?.length && (
            <Tab label={Localize.get('EventDetails.participationDocuments')} />
          )}
        </Tabs>

        <Formik
          initialValues={initialValues}
          onSubmit={() => {
            onClose();
          }}
        >
          {() => (
            <Form>
              <TabPanel value={tabIndex} index={0} sx={{ p: 2 }}>
                <GeneralInformationTab titles={titles} genders={genders} languages={languages} />
              </TabPanel>

              <TabPanel value={tabIndex} index={1} sx={{ p: 2 }}>
                <ContactInformationTab
                  titles={titles}
                  countries={countriesGeneral}
                  regions={regionsGeneral}
                  countriesCommunication={countriesCommunication}
                  regionsCommunication={regionsCommunication}
                />
              </TabPanel>

              <TabPanel value={tabIndex} index={2} sx={{ p: 2 }}>
                <AdditionalInformationTab />
              </TabPanel>

              {!!sortedEventConsents?.length && (
                <TabPanel value={tabIndex} index={3} sx={{ p: 2 }}>
                  <ConsentsList sortedEventConsents={sortedEventConsents} />
                </TabPanel>
              )}

              <DialogActions
                sx={(theme) => ({
                  mt: 2,
                  backgroundColor: theme.palette.primary.contrastText,
                  padding: '16px 24px',
                  display: 'none',
                  justifyContent: 'right'
                })}
              >
                <Button onClick={onClose} color='inherit' sx={{ fontWeight: 500 }}>
                  {Localize.get('App.cancel')}
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  sx={(theme) => ({ backgroundColor: theme.palette.primary.main })}
                >
                  {Localize.get('Booking.saveParticipant')}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default BookingParticipantDialog;
